import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { dayPropertiesApi } from 'api/day-properties/day-properties.api';
import type { IState } from 'redux/state';
import type { Thunk } from 'types/redux';
import { sortBy } from 'utils/typesafe';
import type {
  DayProperties,
  DayPropertiesFilter,
  DayPropertiesParams,
} from './dayProperties.schema';

export const dayPropertiesAdapter = createEntityAdapter<DayProperties>();

export const fetchDayProperties = createAsyncThunk(
  'dayProperties/fetch',
  async (filters: DayPropertiesFilter | undefined = {}) => {
    return await dayPropertiesApi.get(filters);
  }
);

/**
 * @param staleTimeInSeconds - how long the data is considered fresh (== how long should we serve it from cache)
 */
export const fetchDayPropertiesSmartly = (
  filters: DayPropertiesFilter,
  staleTimeInSeconds: number = 300 // 5 min
): Thunk => {
  return (dispatch, getState) => {
    const state = getState() as IState;
    const fetchedKeys = state.dayProperties.fetchedKeys;
    const key = getFetchKeyFromFilters(filters);
    if (key && fetchedKeys[key]) {
      if (staleTimeInSeconds && fetchedKeys[key] + staleTimeInSeconds * 1000 < Date.now()) {
        return dispatch(fetchDayProperties(filters));
      }
      return Promise.resolve();
    }
    return dispatch(fetchDayProperties(filters));
  };
};

export const saveDayProperties = createAsyncThunk<Array<DayProperties>, DayPropertiesParams>(
  'dayProperties/save',
  async params => await dayPropertiesApi.save(params)
);

const initialState = dayPropertiesAdapter.getInitialState({
  isLoading: false,
  fetchedKeys: {} as Record<string, number>,
});

export type DayPropertiesState = typeof initialState;

const dayPropertiesStateSlice = createSlice({
  name: 'dayProperties',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchDayProperties.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchDayProperties.fulfilled, (state, action) => {
        dayPropertiesAdapter.upsertMany(state, action.payload);
        const filters = action.meta.arg;
        if (filters && Object.keys(filters).length) {
          const key = serializeFilters(filters);
          state.fetchedKeys[key] = Date.now();
        }
        state.isLoading = false;
      })
      .addCase(fetchDayProperties.rejected, state => {
        state.isLoading = false;
      })
      .addCase(saveDayProperties.fulfilled, (state, action) => {
        dayPropertiesAdapter.upsertMany(state, action.payload);
      });
  },
});

export const isDayLockedForUser = (
  userId: number,
  dayProperties: Array<DayProperties> | undefined
) => !!dayProperties && dayProperties.some(p => p.user_id === userId && p.locked);

const getFetchKeyFromFilters = (filters: DayPropertiesFilter) => {
  if (filters && Object.keys(filters).length) {
    return serializeFilters(filters);
  }
  return null;
};

export const serializeFilters = (filters: DayPropertiesFilter): string => {
  // Ensure that object properties and their values are always serialized in the same order
  const sortedFilters = Object.keys(filters)
    .sort()
    .reduce(
      (acc, key) => {
        const filterKey = key as keyof DayPropertiesFilter;
        const value = filters[filterKey];
        acc[filterKey] = Array.isArray(value) ? sortBy<number | string>(value) : filters[filterKey];
        return acc;
      },
      {} as Record<string, unknown>
    );

  return JSON.stringify(sortedFilters);
};

export const { actions } = dayPropertiesStateSlice;

export default dayPropertiesStateSlice.reducer;
